import React, { useState, useEffect } from 'react';
import { forumHelpers, ForumCategory, ForumTag } from '../lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Plus, Edit, Trash2, Upload, X, Check, Palette, Type } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface CategoryManagerProps {
  onClose: () => void;
  onCategoriesUpdated: () => void;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({ onClose, onCategoriesUpdated }) => {
  const { user } = useAuth();
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Estados do formulário
  const [isCreating, setIsCreating] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ForumCategory | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#0FFF55',
    icon: '💬',
    banner_image: '',
    custom_tag: ''
  });


  


  // Cores predefinidas para categorias
  const predefinedColors = [
    '#0FFF55', '#3B82F6', '#EF4444', '#F59E0B', '#8B5CF6', 
    '#EC4899', '#10B981', '#F97316', '#06B6D4', '#84CC16'
  ];

  // Ícones predefinidos
  const predefinedIcons = [
    '💬', '💡', '🚀', '📚', '🎯', '🔧', '💼', '🎨', '🎮', '📱',
    '💻', '🌐', '📊', '🎪', '🏆', '⭐', '🔥', '💎', '🎭', '🎵'
  ];

  useEffect(() => {
    loadCategories();
  }, []);



  const loadCategories = async () => {
    try {
      setLoading(true);
      console.log('Carregando categorias...');
      const { data, error } = await forumHelpers.getCategories();
      
      if (error) {
        setError('Erro ao carregar categorias');
        console.error('Erro ao carregar categorias:', error);
      } else {
        console.log('Categorias carregadas:', data);
        setCategories(data || []);
      }
    } catch (err) {
      setError('Erro inesperado ao carregar categorias');
      console.error('Erro inesperado:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '#0FFF55',
      icon: '💬',
      banner_image: '',
      custom_tag: ''
    });
    setIsCreating(false);
    setEditingCategory(null);
  };

  const handleCreateCategory = () => {
    setIsCreating(true);
    setEditingCategory(null);
    resetForm();
  };

  const handleEditCategory = (category: ForumCategory) => {
    console.log('Editando categoria:', category);
    setEditingCategory(category);
    setIsCreating(false);
    setFormData({
      name: category.name,
      description: category.description || '',
      color: category.color || '#0FFF55',
      icon: category.icon || '💬',
      banner_image: category.banner_url || category.banner_image || '',
      custom_tag: category.custom_tag || ''
    });
  };



  const handleSubmit = async () => {
    if (!user || !formData.name.trim()) {
      setError('Preencha o nome da categoria');
      return;
    }

    // Validação de banner obrigatório
    if (!formData.banner_image.trim()) {
      setError('⚠️ Banner é obrigatório! Adicione uma URL de imagem para o banner da categoria.');
      return;
    }

    try {
      setError(null); // Limpar erros anteriores
      setSuccess(null); // Limpar sucessos anteriores
      
      const categoryData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        color: formData.color,
        icon: formData.icon,
        banner_url: formData.banner_image.trim(), // Usar banner_url em vez de banner_image
        custom_tag: formData.custom_tag.trim() || undefined,
        is_active: true,
        created_by: user.id
      };

      console.log('Salvando categoria com dados:', categoryData);

      let result;
      
      if (editingCategory) {
        // Atualizar categoria existente
        console.log('Atualizando categoria:', editingCategory.id);
        result = await forumHelpers.updateCategory(editingCategory.id, categoryData);
      } else {
        // Criar nova categoria
        console.log('Criando nova categoria');
        result = await forumHelpers.createCategory(categoryData);
      }

      console.log('Resultado da operação:', result);

      if (result.error) {
        console.error('Erro:', result.error);
        
        // Tratamento específico para erro de coluna
        if (result.error.message && result.error.message.includes('banner_url')) {
          setError('⚠️ Erro no banco de dados: Coluna banner_url não encontrada. Entre em contato com o administrador.');
        } else {
          setError(`Erro ao ${editingCategory ? 'atualizar' : 'criar'} categoria: ${result.error.message || 'Erro desconhecido'}`);
        }
      } else if (result.data) {
        console.log('Categoria salva com sucesso:', result.data);
        // Recarregar categorias
        await loadCategories();
        onCategoriesUpdated();
        setSuccess(`Categoria ${editingCategory ? 'atualizada' : 'criada'} com sucesso!`);
        resetForm();
      }
    } catch (err) {
      console.error('Erro inesperado:', err);
      setError('Erro inesperado');
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    try {
      setError(null); // Limpar erros anteriores
      setSuccess(null); // Limpar sucessos anteriores
      
      console.log('Tentando excluir categoria:', categoryId);
      
      // Deletar a categoria
      const result = await forumHelpers.deleteCategory(categoryId);
      
      console.log('Resultado da exclusão:', result);
      
      if (result.error) {
        console.error('Erro:', result.error);
        setError(`Erro ao excluir categoria: ${result.error.message || 'Erro desconhecido'}`);
      } else if (result.success) {
        console.log('Categoria excluída com sucesso');
        // Recarregar categorias após exclusão bem-sucedida
        await loadCategories();
        onCategoriesUpdated();
        setSuccess('Categoria excluída com sucesso!');
      } else {
        setError('Erro ao excluir categoria: Operação não foi bem-sucedida');
      }
    } catch (err) {
      console.error('Erro inesperado ao excluir categoria:', err);
      setError('Erro inesperado ao excluir categoria');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#0FFF55]"></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[#121316] rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#1f2329]">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">
              Gerenciar Categorias
            </h2>
            <Button
              variant="ghost"
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
              <p className="text-green-400">{success}</p>
            </div>
          )}

          {/* Botão para criar nova categoria */}
          <div className="mb-6">
            <Button
              onClick={handleCreateCategory}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Nova Categoria
            </Button>
          </div>

          {/* Formulário de criação/edição */}
          {(isCreating || editingCategory) && (
            <Card className="mb-6 bg-[#1a1d23] border-[#1f2329]">
              <CardHeader>
                <CardTitle className="text-white">
                  {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-white">Nome</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Nome da categoria"
                      className="bg-[#0f1114] border-[#1f2329] text-white"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="description" className="text-white">Descrição</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Descrição da categoria"
                      className="bg-[#0f1114] border-[#1f2329] text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Cor</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {predefinedColors.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={`w-8 h-8 rounded-full border-2 transition-all ${
                            formData.color === color 
                              ? 'border-white scale-110' 
                              : 'border-gray-600 hover:border-gray-400'
                          }`}
                          style={{ backgroundColor: color }}
                          onClick={() => setFormData({ ...formData, color })}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-white">Ícone</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {predefinedIcons.map((icon) => (
                        <button
                          key={icon}
                          type="button"
                          className={`w-10 h-10 rounded-lg border-2 text-lg transition-all ${
                            formData.icon === icon 
                              ? 'border-[#0FFF55] bg-[#0FFF55]/20' 
                              : 'border-gray-600 hover:border-gray-400'
                          }`}
                          onClick={() => setFormData({ ...formData, icon })}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-white">Etiqueta da Categoria</Label>
                  <div className="mt-2">
                    <Input
                      type="text"
                      value={formData.custom_tag || ''}
                      onChange={(e) => setFormData({ ...formData, custom_tag: e.target.value })}
                      placeholder="Ex: Ativa, Em Destaque, Nova, Popular..."
                      className="w-full bg-[#0f1114] border border-[#1f2329] text-white rounded-md px-3 py-2"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Digite uma etiqueta personalizada que será exibida no lugar de "Ativa"
                    </p>
                  </div>
                </div>

                <div>
                  <Label className="text-white">URL da Imagem do Banner</Label>
                  <div className="mt-2 space-y-2">
                    <Input
                      type="url"
                      value={formData.banner_image}
                      onChange={(e) => setFormData({ ...formData, banner_image: e.target.value })}
                      placeholder="https://exemplo.com/imagem.jpg"
                      className="bg-[#0f1114] border-[#1f2329] text-white"
                    />
                    
                    {/* Preview da imagem */}
                    {formData.banner_image && (
                      <div className="relative w-full h-32 rounded-lg overflow-hidden border border-[#1f2329]">
                        <img
                          src={formData.banner_image}
                          alt="Preview"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                        <div className="hidden w-full h-full flex items-center justify-center text-gray-500 text-sm bg-[#0f1114]">
                          Imagem não encontrada
                        </div>
                      </div>
                    )}

                    {/* Instruções para URLs de imagem */}
                    <div className="bg-[#0f1114] rounded-lg p-3 border border-[#1f2329]">
                      <h4 className="text-white font-medium mb-2 text-sm">💡 Como obter URLs de imagem:</h4>
                      <div className="space-y-2 text-xs text-gray-300">
                        <div>
                          <strong className="text-[#0FFF55]">GitHub:</strong>
                          <p>1. Faça upload da imagem no seu repositório</p>
                          <p>2. Clique na imagem → "Raw" → Copie a URL</p>
                          <p className="text-gray-400">Ex: https://raw.githubusercontent.com/usuario/repo/main/images/banner.jpg</p>
                        </div>
                        <div>
                          <strong className="text-[#0FFF55]">Google Drive:</strong>
                          <p>1. Faça upload da imagem no Drive</p>
                          <p>2. Clique com botão direito → "Obter link compartilhável"</p>
                          <p>3. Copie o ID e use: https://drive.google.com/uc?export=view&id=ID_AQUI</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleSubmit}
                    className="flex items-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    {editingCategory ? 'Atualizar' : 'Criar'} Categoria
                  </Button>
                  <Button
                    variant="outline"
                    onClick={resetForm}
                  >
                    Cancelar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lista de categorias */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Categorias Existentes</h3>
            
            {categories.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Type className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma categoria criada ainda</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category) => (
                  <Card
                    key={category.id}
                    className="bg-[#1a1d23] border-[#1f2329] hover:border-[#0FFF55]/20 transition-colors"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center text-lg"
                            style={{ backgroundColor: category.color + '20', color: category.color }}
                          >
                            {category.icon}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-white">{category.name}</h4>
                            {category.description && (
                              <p className="text-sm text-gray-400 line-clamp-2">
                                {category.description}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditCategory(category)}
                            className="text-gray-400 hover:text-white"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-[#1a1d23] border-[#1f2329]">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-white">
                                  Excluir Categoria
                                </AlertDialogTitle>
                                <AlertDialogDescription className="text-gray-400">
                                  Tem certeza que deseja excluir a categoria "{category.name}"? 
                                  Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-[#0f1114] border-[#1f2329] text-white">
                                  Cancelar
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteCategory(category.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Excluir
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryManager; 